// OGC-531 Creatives Manager dialog (v19): a native modal <dialog> for
// confirmations, dependency explanations, forms and previews. Escape and
// Cancel close it unless an action is in flight; focus starts on the action
// marked `autoFocus` (the safe choice) and returns to the element that opened
// it, or to the page heading when that element is gone.
import { useEffect, useId, useLayoutEffect, useRef } from 'react';

const useIsomorphicLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;

function focusTarget(element) {
  if (!element || element.isConnected === false || typeof element.focus !== 'function') return false;
  element.focus({ preventScroll: true });
  return document.activeElement === element;
}

export default function CatalogDialog({
  open,
  title,
  description,
  children,
  actions = [],
  error = '',
  busy = false,
  onClose,
  className = '',
  returnFocus = null,
  initialFocus = null,
}) {
  const dialogRef = useRef(null);
  const openerRef = useRef(null);
  const titleId = useId();
  const descriptionId = useId();

  useIsomorphicLayoutEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (open && !dialog.open) {
      openerRef.current = returnFocus || document.activeElement;
      if (typeof dialog.showModal === 'function') dialog.showModal();
      else dialog.setAttribute('open', '');
      const target = (initialFocus && dialog.querySelector(initialFocus))
        || dialog.querySelector('[data-autofocus="true"]');
      focusTarget(target);
    } else if (!open && dialog.open) {
      dialog.close();
      const opener = openerRef.current;
      openerRef.current = null;
      if (!focusTarget(opener)) focusTarget(document.querySelector('.creatives-manager h1'));
    }
  }, [open]);

  // One open dialog can move to the next step (for example "Archive
  // instead"): start the new step on its safe action too.
  const shownTitle = useRef(title);
  useIsomorphicLayoutEffect(() => {
    const dialog = dialogRef.current;
    if (!open || !dialog?.open || shownTitle.current === title) {
      shownTitle.current = title;
      return;
    }
    shownTitle.current = title;
    const target = (initialFocus && dialog.querySelector(initialFocus)) || dialog.querySelector('[data-autofocus="true"]');
    focusTarget(target);
  }, [open, title]);

  // Callers usually close by unmounting the dialog: restore focus then too.
  // The ref is detached before this cleanup runs, so keep the node and the
  // open state from mount. React may run this cleanup after the caller has
  // already moved focus on purpose (e.g. to the heading after a delete), so
  // restore only focus that was lost with the dialog.
  const openRef = useRef(false);
  openRef.current = Boolean(open);
  useEffect(() => {
    const dialog = dialogRef.current;
    return () => {
      if (!openRef.current) return;
      if (dialog?.open) dialog.close();
      const opener = openerRef.current;
      openerRef.current = null;
      const active = document.activeElement;
      if (active && active !== document.body && !dialog?.contains(active)) return;
      if (!focusTarget(opener)) focusTarget(document.querySelector('.creatives-manager h1'));
    };
  }, []);

  return (
    <dialog
      ref={dialogRef}
      className={`cm-dialog${className ? ` ${className}` : ''}`}
      aria-labelledby={titleId}
      aria-describedby={description ? descriptionId : undefined}
      aria-busy={busy ? 'true' : undefined}
      onCancel={(event) => {
        event.preventDefault();
        if (!busy) onClose?.();
      }}
      // Chrome can still close a modal natively (a repeated Escape). Keep the
      // element in step with `open`: reopen while busy, otherwise treat it as
      // a cancel so later dialogs can open again.
      onClose={(event) => {
        if (!openRef.current) return;
        const dialog = event.currentTarget;
        if (busy) {
          if (!dialog.open && typeof dialog.showModal === 'function') dialog.showModal();
          return;
        }
        onClose?.();
      }}
    >
      {open ? (
        <>
          <h2 id={titleId}>{title}</h2>
          {description ? <p id={descriptionId}>{description}</p> : null}
          {children}
          <p className="field-error" role="alert">{error}</p>
          <div className="dialog-actions">
            {actions.map((action) => (
              <button
                key={action.label}
                type={action.type || 'button'}
                form={action.form}
                className={action.tone || undefined}
                data-autofocus={action.autoFocus ? 'true' : undefined}
                // Busy actions stay focusable (aria-disabled): disabling the
                // focused button would drop keyboard focus to the page.
                aria-disabled={busy || action.disabled ? 'true' : undefined}
                onClick={(event) => {
                  if (busy || action.disabled) { event.preventDefault(); return; }
                  action.onClick?.(event);
                }}
              >
                {busy && action.busyLabel ? action.busyLabel : action.label}
              </button>
            ))}
          </div>
        </>
      ) : null}
    </dialog>
  );
}
